import { Injectable, inject } from '@angular/core';
import { AppConfigurationClient, FeatureFlagValue as RemoteFeatureFlagValue } from '@azure/app-configuration';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  concat,
  concatMap,
  delay,
  distinctUntilChanged,
  filter,
  map,
  of,
  share,
  switchMap,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { IxRestApi } from 'src/app/core/services/api/ix-rest-api';
import { getConfigurationValue$ } from '../api-fns';
import { FeatureFlag, FeatureFlagAdapter } from './feature-toggle.service';
import { IxReleaseBanner, IxReleaseBannerDict, getBannerSignature } from './release-banner.service';

const RemoteFeatureFlagKeyPrefix = '.appconfig.featureflag';
const EmptyConnectionString = 'Endpoint=;Id=;Secret=';
const RemoteConfigurationReleaseBannerKeyPrefix = '.appconfig.banner.release.';
// First banner fetch is intentionally delayed to avoid competing with initial app startup calls.
const InitialBannerFetchDelayMs = 5_000;
// Poll every 5 minutes by default, and back off in +5 minute steps when data is unchanged.
const InitialBannerPollingMinutes = 5;
const BannerPollingIncrementMinutes = 5;
const MillisecondsPerMinute = 60000;

interface GetFeatureFlagsResponse {
  items: { key: string; value: string; last_modified: Date }[];
}

@Injectable({ providedIn: 'root' })
export class AzureFeatureFlagService implements FeatureFlagAdapter {
  readonly #ixRestApi = inject(IxRestApi);
  readonly #client$ = new ReplaySubject<AppConfigurationClient>(1);
  // Drives recurring polling cadence and can be updated dynamically after each response.
  readonly #bannerPollingMinutes$ = new BehaviorSubject(InitialBannerPollingMinutes);
  // Stores the previous response signature to detect whether there is new banner data.
  #lastBannerResponseSignature: string | null = null;
  readonly #azConfigResponse$ = this.#client$.pipe(
    switchMap(
      client =>
        client.getConfigurationSetting({
          label: '',
          key: '',
        }) as unknown as Observable<GetFeatureFlagsResponse>,
    ),
    share(),
  );

  readonly allFlags$: Observable<FeatureFlag> = this.#azConfigResponse$.pipe(
    map(response =>
      response.items
        .filter(item => item.key.startsWith(RemoteFeatureFlagKeyPrefix))
        .map(toFeatureFlag)
        .reduce((flags, currentFlag) => ({ ...flags, ...currentFlag }), {} as FeatureFlag),
    ),
  );

  readonly #isReleaseBannerEnabled$ = this.allFlags$.pipe(
    map(flags => Boolean(flags.ReleaseBanner)),
    distinctUntilChanged(),
  );

  readonly #azConfigBannersResponse$ = this.#isReleaseBannerEnabled$.pipe(
    filter(isEnabled => isEnabled), // Skip banner calls entirely if the feature flag is off.
    withLatestFrom(this.#client$),
    delay(InitialBannerFetchDelayMs), // Initial delay to avoid competing with app startup.
    switchMap(([isReleaseBannerEnabled, client]) =>
      isReleaseBannerEnabled
        ? this.#pollBannerConfigurations(client)
        : of({ items: [] } as GetFeatureFlagsResponse),
    ),
  );

  readonly allReleaseBanners$: Observable<IxReleaseBannerDict> = this.#azConfigBannersResponse$.pipe(
    map(response =>
      response.items
        .filter(item => item.key.startsWith(RemoteConfigurationReleaseBannerKeyPrefix))
        .map(toReleaseBanner)
        .reduce((flags, currentFlag) => ({ ...flags, ...currentFlag }), {} as IxReleaseBannerDict),
    ),
  );
  constructor() {
    getConfigurationValue$(this.#ixRestApi, 'featureflag', EmptyConnectionString).subscribe(
      connectionString => this.#client$.next(new AppConfigurationClient(connectionString)),
    );
  }

  #pollBannerConfigurations(client: AppConfigurationClient): Observable<GetFeatureFlagsResponse> {
    return concat(
      // First call emits immediately once this method is entered.
      this.#fetchBannerConfigurations(client),
      this.#bannerPollingMinutes$.pipe(
        // Resubscribe timer whenever interval changes so the new cadence takes effect.
        switchMap(intervalMinutes =>
          timer(this.#minutesToMs(intervalMinutes), this.#minutesToMs(intervalMinutes)),
        ),
        concatMap(() => this.#fetchBannerConfigurations(client)),
      ),
    ).pipe(tap(response => this.#updateBannerPollingInterval(response)));
  }

  #fetchBannerConfigurations(client: AppConfigurationClient): Observable<GetFeatureFlagsResponse> {
    return client.getConfigurationSetting({
      label: 'ix-banner', // This label is used to separate release banner configurations from feature flags in Azure. It must be applied to any newly created banner configuration items in Azure to be picked up by the app.
      key: '',
    }) as unknown as Observable<GetFeatureFlagsResponse>;
  }

  #updateBannerPollingInterval(response: GetFeatureFlagsResponse): void {
    const responseSignature = response.items
      .map(item => getBannerSignature(item.key, new Date(item.last_modified)))
      .sort()
      .join('|');
    const hasNewData = responseSignature !== this.#lastBannerResponseSignature;
    const currentIntervalMinutes = this.#bannerPollingMinutes$.value;

    this.#lastBannerResponseSignature = responseSignature;
    const nextIntervalMinutes = hasNewData
      ? InitialBannerPollingMinutes
      : currentIntervalMinutes + BannerPollingIncrementMinutes;
    // Reset to baseline if data changed; otherwise back off to reduce unnecessary calls.
    this.#bannerPollingMinutes$.next(nextIntervalMinutes);
  }

  #minutesToMs(minutes: number): number {
    return minutes * MillisecondsPerMinute;
  }
}

function toFeatureFlag(flag: { key: string; value: string }): FeatureFlag {
  return {
    [flag.key.replace(`${RemoteFeatureFlagKeyPrefix}/`, '')]: JSON.parse(flag.value)
      .enabled as RemoteFeatureFlagValue,
  } as unknown as FeatureFlag; // Type bailout because we cannot verify this ahead of time
}

function toReleaseBanner(flag: { key: string; value: string; last_modified: Date }): IxReleaseBannerDict {
  const bannerData = JSON.parse(flag.value) as IxReleaseBanner;
  return {
    [flag.key.replace(`${RemoteConfigurationReleaseBannerKeyPrefix}`, '')]: {
      ...bannerData,
      lastModified: flag.last_modified,
    },
  } as unknown as IxReleaseBannerDict; // Type bailout because we cannot verify this ahead of time
}
